/**
 * The DOM handles the dynamic proposal form exposes to anything that has to
 * ADDRESS one of its controls — a Playwright spec, and later the round-trip
 * rewriter (CZNA-962).
 *
 * One module, because the alternative is what the form actually had: the
 * subfield input derived its `id` inline in `DynamicFieldPanel`, the picker row
 * carried a `data-testid` built from the subfield code ALONE (so two instances
 * of `370` collided, and so did `$a` in two different fields), the indicator was
 * a bare `<select>` with neither `id` nor an associated label, and a component
 * subfield (`100$a` = Příjmení + Jméno) had no handle at all — it was reachable
 * only by its Czech label. Labels live in `semantic-catalog.json` and move: one
 * rename already reddened a spec that had no other way in (CZNA-619).
 *
 * Every derivation here is a pure function of the react-hook-form path, which is
 * the only thing an addressing scheme may depend on — the path is what the form
 * itself is built from, so a handle derived from it cannot drift away from the
 * control it names.
 *
 * NOT the MARC coordinates. `pathPrefix` is `fields.<stateKey>.<rhfIndex>`, and
 * `rhfIndex` is the position in the react-hook-form field array, NOT the MARC
 * `field_index` the payload carries. The two agree today for a single-tag field
 * and diverge as soon as one MARC tag is reached through two `fieldId` groups
 * (`024-isni` + `024-wikidata` are both tag `024`), because
 * `genericFormToProposalData` allocates `field_index` per TAG. Mapping one onto
 * the other is the caller's job.
 */
/** Normalises an RHF path into something legal in an `id` attribute. */
var flatten = function (path) { return path.replace(/\./g, '-'); };
/**
 * `id` of the element holding ONE subfield's value: a plain input (text,
 * textarea, select, combobox, action-button) or — CZNA-1048 — the chip
 * container of a multi-value subfield.
 *
 * Shape: `sf-fields-<stateKey>-<rhfIndex>-<code>`. Already relied on by
 * `tests/e2e/tests/czna-684-submit-from-form.spec.ts`, so the shape is fixed —
 * this function only gives it one definition instead of an inline expression.
 *
 * The chip list is the same handle on a different KIND of element, and that
 * difference is the useful part rather than an inconsistency: a chip container
 * is a `<div>`, so the locator can be counted and read but not typed into. It
 * was added because the chip list had no handle at all, and a spec reduced to
 * addressing its label span could only assert over the label's own text — so
 * `#sf-…-c` resolved to zero elements and `toHaveCount(0)` over it held with the
 * chip list plainly on screen (the vacuous assertion CZNA-1044 shipped).
 * `MultiSelectVocabularySubfield` takes it as a prop; both of its render
 * branches (free-text and picker/hybrid) put it on their chip container.
 */
export var subfieldInputId = function (pathPrefix, code) {
    return flatten("sf-".concat(pathPrefix, "-").concat(code));
};
/** `id` of the `<datalist>` backing a combobox subfield. */
export var subfieldDatalistId = function (pathPrefix, code) {
    return flatten("datalist-".concat(pathPrefix, "-").concat(code));
};
/**
 * `id` of one COMPONENT input inside a component-bearing subfield — `100$a`
 * splits into FAMILY_NAME and GIVEN_NAME, `100$d` into BIRTH_DATE and
 * DEATH_DATE.
 *
 * Suffixed by `componentType` rather than by render order: the order is
 * filtered per heading type (`visibleWhen.ind1`), so an index would name a
 * different component for `ind1=1` than for `ind1=3`.
 */
export var componentInputId = function (pathPrefix, code, componentType) { return flatten("sf-".concat(pathPrefix, "-").concat(code, "-").concat(componentType)); };
/**
 * `id` of the `<datalist>` backing a component input whose
 * `componentInputType` is `select` — `100$a`'s FAMILY_QUALIFIER (CZNA-1007).
 *
 * Its own derivation rather than `subfieldDatalistId(pathPrefix, code)`,
 * because two components of ONE subfield would otherwise share a list id and
 * the second `<datalist>` would be dead markup.
 */
export var componentDatalistId = function (pathPrefix, code, componentType) { return flatten("datalist-".concat(pathPrefix, "-").concat(code, "-").concat(componentType)); };
/**
 * `id` of an indicator `<select>` for a field instance.
 *
 * Not derived through `subfieldInputId` with a `_ind1` code: an indicator is not
 * a subfield, and `sf-…--ind1` (the double dash the RHF slot name would produce)
 * is a handle nobody would guess.
 */
export var indicatorSelectId = function (pathPrefix, which) {
    return flatten("ind".concat(which, "-").concat(pathPrefix));
};
/**
 * `id` of the ⋮ menu trigger on one subfield row (CZNA-1422). `rowKey` is the
 * row's key in `subfieldRowLayout`: the subfield code for a control, `pt<n>`
 * for the n-th carried row — so the handle follows the row when it moves.
 */
export var subfieldRowMenuId = function (pathPrefix, rowKey) {
    return flatten("row-menu-".concat(pathPrefix, "-").concat(rowKey));
};
/**
 * `id` of the input of a carried row the cataloguer may edit (CZNA-1410): a
 * subfield the template does not declare. Keyed like {@link subfieldRowMenuId},
 * by the row's key, because the code alone is not unique — a repeatable code
 * can be added twice.
 */
export var carriedSubfieldInputId = function (pathPrefix, rowKey) {
    return flatten("sf-carried-".concat(pathPrefix, "-").concat(rowKey));
};
/**
 * `id` of the "Přidat podpole…" combobox at the end of one field instance
 * (CZNA-1410, a combobox since CZNA-1449).
 */
export var addSubfieldMenuId = function (pathPrefix) {
    return flatten("add-subfield-".concat(pathPrefix));
};
/** `id` of the combobox "Vložit podpole za…" opens right after the row `rowKey` (CZNA-1449). */
export var insertSubfieldComboboxId = function (pathPrefix, rowKey) {
    return flatten("insert-subfield-".concat(pathPrefix, "-").concat(rowKey));
};
/** `id` of the ⋮ in the header of one occurrence of a repeatable field (CZNA-1449). */
export var fieldInstanceMenuId = function (pathPrefix) {
    return flatten("field-menu-".concat(pathPrefix));
};
/** `id` of the dashed outline that adds the next occurrence of a repeatable field (CZNA-1449). */
export var addFieldInstanceId = function (fieldPath) {
    return flatten("add-field-".concat(fieldPath));
};
